/**
 * Searchanise → Horizon native add-to-cart feedback (no cart drawer).
 *
 * Mirrors product-form / add-to-cart-component behavior:
 * - Button: AddToCartComponent.animateAddToCart() toggles `atc-added` on the button
 *   (see assets/product-form.js, snippets/add-to-cart-button.liquid).
 * - Checkmark + "Added": `.add-to-cart-text` / `.add-to-cart-text--added` + `.atc-added` CSS.
 * - Fly orb: `#animateFlyToCart()` creates `<fly-to-cart>` with variant image → `.header-actions__cart-icon`.
 * - Cart bubble: `cart:update` (ThemeEvents.cartUpdate / CartAddEvent.eventName) → CartIcon.onCartUpdate.
 *
 * Searchanise: `Searchanise.AddToCartSuccess` (see Searchanise JS API docs).
 */
(function () {
  const CART_UPDATE = 'cart:update';
  /** Same as ADD_TO_CART_TEXT_ANIMATION_DURATION in assets/product-form.js */
  const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

  const SNIZE_UI_SELECTOR = '[class*="snize"], .snize-search-results, [id*="snize"]';
  const SNIZE_ATC_LOADING_CLASS = 'searchanise-atc-loading';
  /** Failsafe if Searchanise never fires success/error */
  const SNIZE_ATC_LOADING_TIMEOUT_MS = 25000;

  /** @param {string | number} v */
  function escapeAttrValue(v) {
    return String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  /** @type {HTMLElement | null} */
  let lastSnizePointerTarget = null;

  /** @type {HTMLElement | null} */
  let snizeAtcLoadingButton = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  let snizeAtcLoadingTimeoutId = null;

  function isInsideSearchaniseUi(button) {
    return !!button.closest(SNIZE_UI_SELECTOR);
  }

  /** Horizon PDP / quick-add — do not treat as Searchanise instant-search ATC. */
  function isNativeHorizonProductAtcButton(btn) {
    return !!(
      btn.closest('product-form') ||
      btn.closest('add-to-cart-component') ||
      btn.closest('form[data-type="add-to-cart-form"]') ||
      btn.closest('.quick-add-modal')
    );
  }

  function hasSearchaniseProductTileContext(btn) {
    return !!(
      btn.closest('[data-variant-id], [data-product-variant-id], .snize-product, [class*="snize-product"]') ||
      btn.hasAttribute('data-variant-id') ||
      btn.hasAttribute('data-product-variant-id')
    );
  }

  /**
   * Searchanise instant-search product tile ATC only (not theme product-form buttons).
   * @param {HTMLElement} btn
   */
  function isSearchaniseAtcButton(btn) {
    if (!isInsideSearchaniseUi(btn)) return false;
    if (isNativeHorizonProductAtcButton(btn)) return false;
    return hasSearchaniseProductTileContext(btn);
  }

  function clearSnizeAtcLoading() {
    if (snizeAtcLoadingTimeoutId != null) {
      clearTimeout(snizeAtcLoadingTimeoutId);
      snizeAtcLoadingTimeoutId = null;
    }
    if (snizeAtcLoadingButton) {
      snizeAtcLoadingButton.classList.remove(SNIZE_ATC_LOADING_CLASS);
      snizeAtcLoadingButton = null;
    }
  }

  /**
   * @param {HTMLElement} button
   */
  function beginSnizeAtcLoading(button) {
    if (snizeAtcLoadingButton && snizeAtcLoadingButton !== button) {
      snizeAtcLoadingButton.classList.remove(SNIZE_ATC_LOADING_CLASS);
    }
    snizeAtcLoadingButton = button;
    button.classList.add(SNIZE_ATC_LOADING_CLASS);
    if (snizeAtcLoadingTimeoutId != null) clearTimeout(snizeAtcLoadingTimeoutId);
    snizeAtcLoadingTimeoutId = setTimeout(function () {
      snizeAtcLoadingTimeoutId = null;
      if (snizeAtcLoadingButton === button) {
        button.classList.remove(SNIZE_ATC_LOADING_CLASS);
        snizeAtcLoadingButton = null;
      }
    }, SNIZE_ATC_LOADING_TIMEOUT_MS);
  }

  function onSnizeAtcPointerLikeInteraction(/** @type {Event} */ e) {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const btn = t.closest('button, [role="button"], a');
    if (!btn) return;
    if (btn.closest(SNIZE_UI_SELECTOR)) {
      lastSnizePointerTarget = /** @type {HTMLElement} */ (btn);
    }
    if (isSearchaniseAtcButton(/** @type {HTMLElement} */ (btn))) {
      beginSnizeAtcLoading(/** @type {HTMLElement} */ (btn));
    }
  }

  document.addEventListener('pointerdown', onSnizeAtcPointerLikeInteraction, true);
  /** Keyboard / synthetic activations may not fire pointerdown before click. */
  document.addEventListener('click', onSnizeAtcPointerLikeInteraction, true);

  function getBridgeConfig() {
    const script = document.querySelector('script[src*="searchanise-horizon-atc-bridge"]');
    return {
      flyAnimation: script?.dataset.flyAnimation === 'true',
      addedLabel: script?.dataset.addedLabel || 'Added',
    };
  }

  function injectFallbackStylesOnce() {
    if (document.getElementById('searchanise-horizon-atc-fallback-css')) return;
    const style = document.createElement('style');
    style.id = 'searchanise-horizon-atc-fallback-css';
    style.textContent = `
      .searchanise-horizon-atc-fallback {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.35em;
        font: inherit;
        line-height: 1;
        color: inherit;
        animation: searchanise-atc-fallback-in var(--animation-speed, 0.2s) var(--animation-easing, ease) forwards;
        pointer-events: none;
      }
      @keyframes searchanise-atc-fallback-in {
        from { opacity: 0; transform: translateY(0.35em); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Same timing as AddToCartComponent.animateAddToCart (product-form.js).
   * @param {HTMLElement} button
   */
  function animateAtcAddedClass(button) {
    let animationTimeout;
    let cleanupTimeout;
    if (!button.classList.contains('atc-added')) {
      button.classList.add('atc-added');
    }
    animationTimeout = setTimeout(function () {
      cleanupTimeout = setTimeout(function () {
        button.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
    return function cancel() {
      clearTimeout(animationTimeout);
      clearTimeout(cleanupTimeout);
      button.classList.remove('atc-added');
    };
  }

  /**
   * Replaces Searchanise button markup for the success window so only Horizon-style
   * "✓ Added" shows (Searchanise sets "View cart" etc.; appending an overlay kept both visible).
   * @param {HTMLElement} button
   * @param {string} addedLabel
   */
  function applyFallbackAddedOverlay(button, addedLabel) {
    injectFallbackStylesOnce();
    const prevHtml = button.innerHTML;
    const prevPosition = button.style.position;
    const prevDisplay = button.style.display;
    const prevAlignItems = button.style.alignItems;
    const prevJustifyContent = button.style.justifyContent;

    if (getComputedStyle(button).position === 'static') {
      button.style.position = 'relative';
    }
    button.innerHTML = '';
    button.style.display = 'flex';
    button.style.alignItems = 'center';
    button.style.justifyContent = 'center';

    const overlay = document.createElement('span');
    overlay.className = 'searchanise-horizon-atc-fallback';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.textContent = '\u2713 ' + addedLabel;
    button.appendChild(overlay);

    function restore() {
      button.innerHTML = prevHtml;
      button.style.position = prevPosition;
      button.style.display = prevDisplay;
      button.style.alignItems = prevAlignItems;
      button.style.justifyContent = prevJustifyContent;
    }

    const t = setTimeout(restore, ADD_TO_CART_TEXT_ANIMATION_DURATION);
    return function () {
      clearTimeout(t);
      restore();
    };
  }

  /**
   * @param {HTMLElement | null} button
   * @param {string} addedLabel
   */
  function runButtonSuccessFeedback(button, addedLabel) {
    if (!button) return;
    const useHorizonAtcClasses =
      !isInsideSearchaniseUi(button) && button.querySelector('.add-to-cart-text--added');
    if (useHorizonAtcClasses) {
      animateAtcAddedClass(button);
    } else {
      applyFallbackAddedOverlay(button, addedLabel);
    }
  }

  /**
   * @param {string | number | undefined} variantId
   * @returns {HTMLElement | null}
   */
  function findSearchaniseAddButton(variantId) {
    if (variantId == null) return null;
    const v = String(variantId);

    if (lastSnizePointerTarget && document.body.contains(lastSnizePointerTarget)) {
      const card = lastSnizePointerTarget.closest('[data-variant-id], [data-product-variant-id], .snize-product, [class*="snize-product"]');
      const cardVid =
        card?.getAttribute('data-variant-id') ||
        card?.getAttribute('data-product-variant-id') ||
        card?.getAttribute('data-id');
      if (!cardVid || cardVid === v) {
        return lastSnizePointerTarget;
      }
    }

    const safe = escapeAttrValue(v);
    const direct = document.querySelector(
      `[data-variant-id="${safe}"] button, [data-variant-id="${safe}"] a, [data-product-variant-id="${safe}"] button`
    );
    if (direct instanceof HTMLElement) return direct;

    const scope = document.querySelector('.snize-search-results, [class*="snize-search"], [id*="snize"]');
    if (!scope) return null;
    const nodes = scope.querySelectorAll('[data-variant-id], [data-product-variant-id]');
    for (const node of nodes) {
      const id =
        node.getAttribute('data-variant-id') || node.getAttribute('data-product-variant-id');
      if (id !== v) continue;
      const btn = node.querySelector('button, a[href]');
      if (btn instanceof HTMLElement) return btn;
    }
    return null;
  }

  /**
   * @param {Record<string, unknown> | undefined} product
   */
  function getProductImageUrl(product) {
    if (!product || typeof product !== 'object') return '';
    const p = /** @type {Record<string, unknown>} */ (product);
    const first =
      (typeof p.image_link === 'string' && p.image_link) ||
      (typeof p.image === 'string' && p.image) ||
      (typeof p.thumbnail_url === 'string' && p.thumbnail_url) ||
      (typeof p.thumb_url === 'string' && p.thumb_url) ||
      (typeof p.image_url === 'string' && p.image_url) ||
      '';
    if (first) return first;
    const images = p.images;
    if (Array.isArray(images) && images.length > 0 && typeof images[0] === 'string') return images[0];
    return '';
  }

  /**
   * Mirrors AddToCartComponent.#animateFlyToCart (product-form.js) + FlyToCart (same file).
   * @param {HTMLElement} sourceButton
   * @param {string} imageUrl
   * @param {boolean} flyEnabled
   */
  function runFlyToCart(sourceButton, imageUrl, flyEnabled) {
    if (!flyEnabled || !imageUrl) return;
    if (sourceButton.closest('.quick-add-modal')) return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !sourceButton) return;

    customElements.whenDefined('fly-to-cart').then(function () {
      const flyToCartElement = /** @type {HTMLElement & { source?: Element; destination?: Element }} */ (
        document.createElement('fly-to-cart')
      );
      flyToCartElement.style.setProperty('background-image', 'url(' + imageUrl + ')');
      flyToCartElement.source = sourceButton;
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);
    });
  }

  /**
   * Same shape as CartAddEvent detail (assets/events.js) for CartIcon + cart-items.
   * @param {Record<string, unknown>} cart
   */
  function dispatchHorizonCartUpdate(cart) {
    const itemCount = typeof cart.item_count === 'number' ? cart.item_count : 0;
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'searchanise-horizon-atc-bridge',
          data: {
            itemCount,
            source: 'searchanise-horizon-atc-bridge',
          },
        },
      })
    );
  }

  async function afterSearchaniseAdd(event, addToCartId, product) {
    const cfg = getBridgeConfig();
    const addedLabel = cfg.addedLabel;

    let button = null;
    if (event) {
      const rawTarget =
        /** @type {{ originalEvent?: Event; target?: EventTarget | null }} */ (event).originalEvent?.target ??
        /** @type {{ target?: EventTarget | null }} */ (event).target;
      if (rawTarget instanceof Element && rawTarget !== document.documentElement) {
        const closest = rawTarget.closest('button, a');
        if (closest instanceof HTMLElement) button = closest;
      }
    }
    if (!button) {
      button = findSearchaniseAddButton(addToCartId);
    }

    const imageUrl = getProductImageUrl(
      product && typeof product === 'object' ? /** @type {Record<string, unknown>} */ (product) : undefined
    );

    runButtonSuccessFeedback(button, addedLabel);
    if (button) {
      runFlyToCart(button, imageUrl, cfg.flyAnimation);
    }

    try {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error('cart.js failed');
      const cart = await res.json();
      dispatchHorizonCartUpdate(/** @type {Record<string, unknown>} */ (cart));
    } catch {
      /* Cart bubble may stay stale; avoid opening the drawer */
    }
  }

  function bindSearchanise() {
    const sn = window.Searchanise;
    if (!sn || typeof sn.$ !== 'function') return;

    /** @type {string[]} Names vary by Searchanise build; unknown names are harmless with jQuery.on */
    const addToCartFailureEvents = [
      'Searchanise.AddToCartError',
      'Searchanise.AddToCartFail',
      'Searchanise.AddToCartFailed',
    ];

    sn.$(document).on('Searchanise.AddToCartSuccess', function (event, addToCartId, product) {
      queueMicrotask(function () {
        clearSnizeAtcLoading();
        afterSearchaniseAdd(event, addToCartId, product);
      });
    });

    addToCartFailureEvents.forEach(function (evtName) {
      sn.$(document).on(evtName, function () {
        queueMicrotask(clearSnizeAtcLoading);
      });
    });
  }

  if (window.Searchanise && window.Searchanise.$) {
    bindSearchanise();
  } else {
    document.addEventListener('Searchanise.Loaded', bindSearchanise, { once: true });
  }
})();
